import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import DashboardLayout from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import AdminWorkflowOverview from '@/pages/AdminWorkflowOverview';
import { UserPlus, User, Key, Copy, Trash } from '@phosphor-icons/react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const { getAuthHeader, user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [resetLinks, setResetLinks] = useState([]);
  const [open, setOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    password: '',
    role: 'employee'
  });

  const loadUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`, { headers: getAuthHeader() });
      setUsers(response.data);
    } catch (error) {
      toast.error('Failed to load users');
    }
  };

  const loadResetLinks = async () => {
    try {
      const response = await axios.get(`${API}/auth/pending-reset-links`, { headers: getAuthHeader() });
      setResetLinks(response.data || []);
    } catch (error) {
      // silently ignore — non-critical
    }
  };

  useEffect(() => {
    loadUsers();
    loadResetLinks();
    const interval = setInterval(loadResetLinks, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/auth/register`, formData, { headers: getAuthHeader() });
      toast.success('User created successfully');
      setOpen(false);
      setFormData({ email: '', name: '', password: '', role: 'employee' });
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    }
  };

  const getRoleBadge = (role) => {
    const roleMap = {
      admin: 'Admin',
      employee: 'Employee',
      lab_manager: 'Lab Manager',
      operations_manager: 'Operations Manager',
      finance: 'Finance'
    };
    return roleMap[role] || role;
  };

  const handleDelete = async () => {
    if (!userToDelete) return;
    try {
      await axios.delete(`${API}/users/${userToDelete.id}`, { headers: getAuthHeader() });
      toast.success(`Deleted ${userToDelete.email}`);
      setUserToDelete(null);
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-8">
        <div>
          <h1 className="text-4xl md:text-5xl font-extrabold tracking-tighter text-text-primary">
            Admin Dashboard
          </h1>
          <p className="mt-2 text-text-secondary">Ordering workflow overview and user management.</p>
        </div>

        <section>
          <div className="mb-3">
            <h2 className="text-2xl font-bold text-text-primary">Workflow Overview</h2>
            <p className="text-sm text-text-secondary">See where every requisition is in the pipeline and who owns the next action.</p>
          </div>
          <AdminWorkflowOverview />
        </section>

        <section>
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-text-primary">User Management</h2>
            <p className="mt-1 text-text-secondary text-sm">Manage system users and roles</p>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-accent-brand hover:bg-accent-brand-hover text-white" data-testid="create-user-button">
                <UserPlus size={20} className="mr-2" weight="duotone" />
                Create User
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Create New User</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Full Name</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    data-testid="user-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    data-testid="user-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <Input
                    id="password"
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required
                    data-testid="user-password-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="role">Role</Label>
                  <Select value={formData.role} onValueChange={(value) => setFormData({ ...formData, role: value })}>
                    <SelectTrigger data-testid="user-role-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="employee">Employee</SelectItem>
                      <SelectItem value="lab_manager">Lab Manager</SelectItem>
                      <SelectItem value="operations_manager">Operations Manager</SelectItem>
                      <SelectItem value="finance">Finance</SelectItem>
                      <SelectItem value="admin">Admin</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button type="submit" className="w-full bg-accent-brand hover:bg-accent-brand-hover text-white" data-testid="submit-user-button">
                  Create User
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <div className="bg-white border border-border-default rounded">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-surface border-b border-border-default">
                <tr>
                  <th className="text-left p-4 text-xs font-bold uppercase tracking-[0.2em] text-text-secondary">Name</th>
                  <th className="text-left p-4 text-xs font-bold uppercase tracking-[0.2em] text-text-secondary">Email</th>
                  <th className="text-left p-4 text-xs font-bold uppercase tracking-[0.2em] text-text-secondary">Role</th>
                  <th className="text-left p-4 text-xs font-bold uppercase tracking-[0.2em] text-text-secondary">Created</th>
                  <th className="text-right p-4 text-xs font-bold uppercase tracking-[0.2em] text-text-secondary">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id} className="border-b border-border-default hover:bg-surface transition-colors" data-testid={`user-row-${user.id}`}>
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-accent-brand-subtle flex items-center justify-center">
                          <User size={20} className="text-accent-brand" weight="duotone" />
                        </div>
                        <span className="font-medium text-text-primary">{user.name}</span>
                      </div>
                    </td>
                    <td className="p-4 text-text-secondary">{user.email}</td>
                    <td className="p-4">
                      <span className="status-badge status-info">{getRoleBadge(user.role)}</span>
                    </td>
                    <td className="p-4 text-text-secondary">
                      {new Date(user.created_at).toLocaleDateString()}
                    </td>
                    <td className="p-4 text-right">
                      {currentUser?.id !== user.id && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => setUserToDelete(user)}
                          data-testid={`delete-user-button-${user.id}`}
                          title={`Delete ${user.email}`}
                        >
                          <Trash size={18} weight="duotone" />
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pending Password Reset Links — always visible so admins know where to look */}
        <div className="bg-white border border-border-default rounded" data-testid="reset-links-section">
          <div className="p-4 border-b border-border-default bg-amber-50">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <Key size={18} weight="duotone" className="text-amber-700" />
                <h2 className="font-semibold text-text-primary">
                  Pending Password Reset Links ({resetLinks.length})
                </h2>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={loadResetLinks}
                data-testid="refresh-reset-links-button"
              >
                Refresh
              </Button>
            </div>
            <p className="text-xs text-text-tertiary mt-1">
              When a user clicks &ldquo;Forgot password?&rdquo;, their reset link appears here.
              Copy and share it directly if email delivery isn&apos;t working. Each link expires 30 minutes after creation.
            </p>
          </div>
          <div className="overflow-x-auto">
            {resetLinks.length === 0 ? (
              <div className="p-8 text-center text-text-tertiary text-sm" data-testid="no-reset-links">
                <Key size={28} weight="duotone" className="text-text-tertiary mx-auto mb-2" />
                No pending reset links. Ask a user to visit <span className="font-mono">/forgot-password</span> to generate one.
              </div>
            ) : (
              <table className="w-full">
                <thead className="bg-surface border-b border-border-default">
                  <tr>
                    <th className="text-left p-4 text-xs font-bold uppercase tracking-[0.2em] text-text-secondary">User</th>
                    <th className="text-left p-4 text-xs font-bold uppercase tracking-[0.2em] text-text-secondary">Expires</th>
                    <th className="text-right p-4 text-xs font-bold uppercase tracking-[0.2em] text-text-secondary">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {resetLinks.map((link) => (
                    <tr key={link.reset_url} className="border-b border-border-default" data-testid={`reset-link-${link.user_email}`}>
                      <td className="p-4">
                        <div className="font-medium text-text-primary">{link.user_name}</div>
                        <div className="text-sm text-text-tertiary">{link.user_email}</div>
                      </td>
                      <td className="p-4 text-sm text-text-secondary">
                        {new Date(link.expires_at).toLocaleString()}
                      </td>
                      <td className="p-4 text-right">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            navigator.clipboard.writeText(link.reset_url);
                            toast.success('Reset link copied to clipboard');
                          }}
                          data-testid={`copy-reset-link-${link.user_email}`}
                        >
                          <Copy size={14} className="mr-1" /> Copy Link
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
        </section>

        {/* Delete user confirmation dialog */}
        <Dialog open={!!userToDelete} onOpenChange={(o) => !o && setUserToDelete(null)}>
          <DialogContent className="sm:max-w-md" data-testid="delete-user-dialog">
            <DialogHeader>
              <DialogTitle>Delete user?</DialogTitle>
            </DialogHeader>
            <div className="space-y-3 text-sm text-text-secondary">
              <p>
                You&apos;re about to permanently delete{' '}
                <span className="font-semibold text-text-primary">{userToDelete?.name}</span>{' '}
                (<span className="font-mono">{userToDelete?.email}</span>).
              </p>
              <p>
                They will lose access immediately. Their historical requisitions, quotations and POs stay in the system for audit purposes.
              </p>
              <p className="text-red-600 font-medium">This action cannot be undone.</p>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <Button
                variant="outline"
                onClick={() => setUserToDelete(null)}
                data-testid="cancel-delete-user-button"
              >
                Cancel
              </Button>
              <Button
                className="bg-red-600 hover:bg-red-700 text-white"
                onClick={handleDelete}
                data-testid="confirm-delete-user-button"
              >
                <Trash size={16} className="mr-2" weight="duotone" /> Delete user
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default AdminDashboard;
